/**
 * ColumnPicker: the "Columns" button on a template's results. Lists the template's own columns and every other field
 * the run returned, each with a checkbox; the table and the CSV show what is ticked.
 *
 *   <ColumnPicker base={template.columns} extra={discoverFields(rows, template.columns)} visible={keys} onChange={setKeys} />
 *
 * A search box narrows long lists. Escape or a click outside closes it and focus returns to the button.
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import Button from "@/components/ui/Button";
import { useEscape, useLayer } from "@/hooks/useOverlay";
import type { TemplateColumn } from "@/services/templates.service";

interface Props {
  base: TemplateColumn[];
  extra: TemplateColumn[];
  visible: string[];
  onChange: (keys: string[]) => void;
}

export default function ColumnPicker({ base, extra, visible, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  const isTop = useLayer(open);
  const shown = new Set(visible);
  const added = extra.filter(c => shown.has(c.key)).length;

  const close = () => {
    setOpen(false);
    buttonRef.current?.focus();
  };
  useEscape(open, close, isTop);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const q = query.trim().toLowerCase();
  const match = (c: TemplateColumn) => !q || c.label.toLowerCase().includes(q) || c.key.toLowerCase().includes(q);
  const baseShown = useMemo(() => base.filter(match), [base, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const extraShown = useMemo(() => extra.filter(match), [extra, q]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keeps the template's order, with added fields after its columns in the order they were picked.
  const toggle = (key: string) => onChange(shown.has(key) ? visible.filter(k => k !== key) : [...visible, key]);

  const row = (c: TemplateColumn) => (
    <li key={c.key}>
      <label className="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-13 text-fg hover:bg-surface-muted">
        <input type="checkbox" checked={shown.has(c.key)} onChange={() => toggle(c.key)} className="h-3.5 w-3.5 shrink-0 accent-primary" />
        <span className="min-w-0 flex-1 truncate" title={c.key}>
          {c.label}
        </span>
      </label>
    </li>
  );

  return (
    <div className="relative" ref={rootRef}>
      <Button
        ref={buttonRef}
        size="sm"
        variant="secondary"
        leftIcon="lucide:columns-3"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="dialog"
        onClick={() => setOpen(o => !o)}
      >
        Columns
        {added > 0 && (
          <span className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-medium text-white">
            <span className="sr-only">, </span>+{added}
            <span className="sr-only"> added</span>
          </span>
        )}
      </Button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Choose columns"
          className="absolute left-0 z-50 mt-2 flex max-h-[min(460px,70vh)] w-[min(320px,calc(100vw-32px))] flex-col rounded-card border border-line bg-raised shadow-popover"
        >
          <div className="shrink-0 border-b border-line p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <h3 className="text-sm font-semibold text-fg">Columns</h3>
              <button type="button" className="text-13 text-primary-text hover:underline" onClick={() => onChange(base.map(c => c.key))}>
                Reset
              </button>
            </div>
            {base.length + extra.length > 12 && (
              <input
                type="search"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Find a field"
                aria-label="Find a field"
                className="h-8 w-full rounded-control border border-line-strong bg-surface px-2 text-13 text-fg focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            )}
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto p-2">
            {baseShown.length > 0 && (
              <>
                <p className="px-2 pb-1 pt-1 text-xs font-medium text-fg-muted">In this template</p>
                <ul>{baseShown.map(row)}</ul>
              </>
            )}
            {extraShown.length > 0 ? (
              <>
                <p className="px-2 pb-1 pt-3 text-xs font-medium text-fg-muted">More fields from this run</p>
                <ul>{extraShown.map(row)}</ul>
              </>
            ) : (
              !q && <p className="px-2 pt-3 text-13 text-fg-muted">This run has no other fields.</p>
            )}
            {q && baseShown.length + extraShown.length === 0 && <p className="px-2 pt-2 text-13 text-fg-muted">No field matches.</p>}
          </div>
          <p className="shrink-0 border-t border-line px-3 py-2 text-xs text-fg-muted">The CSV has the columns shown. The JSON always has every field.</p>
        </div>
      )}
    </div>
  );
}
